<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <transition name="bounce">
      <b-container class="py-5 custom-container container tradie-profile-view">
        <b-container class="custom-container mt-5">
          <b-breadcrumb
            :items="breadcrumbs"
            class="pl-0 pt-5 mt-5 bg-transparent"
          ></b-breadcrumb>

          <!-- Header -->
          <b-row class="profile-header-row align-items-center">
            <b-col>
              <h2 class="font-weight-bold mb-md-0">
                {{ tradie ? tradie.full_name || '-----' : '-' }}
              </h2>
            </b-col>
            <b-col cols="auto">
              <b-button
                variant="outline-secondary"
                class="bg-white text-dark"
                @click="gotoProject"
              >
                Back to Project
              </b-button>
            </b-col>
          </b-row>

          <b-alert
            v-if="loadError"
            show
            variant="danger"
            class="mt-3"
            data-testid="shared-tradie-load-error"
          >
            {{ loadError }}
          </b-alert>

          <b-alert show class="mt-3 read-only-banner">
            <b-icon-eye class="align-middle mr-1" />
            <span class="align-middle">
              Read-only view - no changes can be made from here.
            </span>
          </b-alert>

          <!-- Tradie has asked to delete their account (notice period) -->
          <b-alert
            v-if="accountDeletionDate"
            show
            variant="danger"
            class="mt-3"
            data-testid="tradie-account-deletion-notice"
          >
            This tradie is deleting their account and all their uploaded files.
            It will be removed on <strong>{{ accountDeletionDate }}</strong
            >.
          </b-alert>

          <!-- Profile -->
          <b-row v-if="tradie">
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <div
                    class="d-flex align-items-start mt-4 profile-detail-flex"
                    style="gap: 32px"
                  >
                    <b-avatar
                      :src="
                        tradie.avatar
                          ? tradie.avatar.thumb_url || tradie.avatar.url
                          : null
                      "
                      class="ml-0 mr-auto"
                      style="width: 180px; height: 240px"
                      rounded
                    />

                    <b-row no-gutters class="profile-details-grid">
                      <b-col cols="12" class="mb-0">
                        <h4>Profile:</h4>
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Name:</span>
                        {{ tradie.full_name }}
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Email:</span>
                        {{ tradie.email }}
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Trade:</span>
                        {{ tradie.trade }}
                      </b-col>

                      <template v-if="tradie.consent_given">
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Date of Birth:</span>
                          {{ dateFormat(tradie.details.birthdate) }}
                        </b-col>
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Gender:</span>
                          {{ tradie.details.gender || 'Prefer not to say' }}
                        </b-col>
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Phone Number:</span>
                          {{
                            formatPhoneWithDialCode(tradie.details.phone_number)
                          }}
                        </b-col>
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">USI:</span>
                          {{ tradie.details.usi }}
                        </b-col>
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Trainee?</span>
                          {{ tradie.details.is_trainee ? 'Yes' : 'No' }}
                        </b-col>
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Aboriginal?</span>
                          {{ tradie.details.is_aboriginal ? 'Yes' : 'No' }}
                        </b-col>
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3"
                        >
                          <span class="font-weight-bold">Address:</span><br />
                          {{ tradie.details.address }}
                        </b-col>
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3"
                        >
                          <span class="font-weight-bold">
                            Medical Conditions:
                          </span>
                          <br />
                          {{ tradie.details.medical_conditions }}
                        </b-col>
                        <b-col
                          v-if="tradie.details"
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">PPE Size:</span><br />
                          {{ tradie.details.ppe_size || '-' }}
                        </b-col>

                        <template v-if="tradie.emergency_contact">
                          <b-col cols="12" class="mt-2 mb-0">
                            <h4>Emergency Contact:</h4>
                          </b-col>
                          <b-col cols="12" md="4" lg="3" class="mb-3">
                            <span class="font-weight-bold">Name:</span>
                            {{
                              tradie.emergency_contact
                                .emergency_contact_full_name
                            }}
                          </b-col>
                          <b-col cols="12" md="4" lg="3" class="mb-3">
                            <span class="font-weight-bold">Email:</span>
                            {{
                              tradie.emergency_contact.emergency_contact_email
                            }}
                          </b-col>
                          <b-col
                            cols="12"
                            md="4"
                            lg="3"
                            class="mb-3 profile-col-half"
                          >
                            <span class="font-weight-bold">Phone Number:</span>
                            {{
                              formatPhoneWithDialCode(
                                tradie.emergency_contact
                                  .emergency_contact_phone_number,
                              )
                            }}
                          </b-col>
                          <b-col
                            cols="12"
                            md="4"
                            lg="3"
                            class="mb-3 profile-col-half"
                          >
                            <span class="font-weight-bold">Relationship:</span>
                            {{
                              tradie.emergency_contact
                                .emergency_contact_relationship
                            }}
                          </b-col>
                        </template>
                      </template>
                      <b-col v-else cols="12">
                        <div class="consent-hidden-banner">
                          <b-icon-shield-lock
                            class="consent-hidden-banner__icon"
                          />
                          <div>
                            <p class="mb-1">
                              Personal details hidden until the tradie accepts
                              the privacy consent.
                            </p>
                            <p class="mb-0">
                              Please contact the worker to accept the privacy
                              consent through the app.
                            </p>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </b-container>
              </b-card>
            </b-container>
          </b-row>

          <!-- Trades (read-only) -->
          <b-row v-if="tradie">
            <b-col cols="12" class="d-flex flex-column mb-0">
              <b-container fluid>
                <b-card class="mt-4 file-details">
                  <b-container fluid>
                    <b-row
                      align-v="center"
                      no-gutters
                      class="section-header-row trades-section-row"
                    >
                      <b-col cols="auto">
                        <h3 class="font-weight-bold mb-0">Trades:</h3>
                      </b-col>
                      <b-col class="px-4 d-flex flex-wrap align-items-center">
                        <b-badge
                          v-for="name in tradeNames"
                          :key="name"
                          variant="primary"
                          class="mr-2 px-3 py-2"
                          style="font-size: 0.95rem"
                        >
                          {{ name }}
                        </b-badge>
                      </b-col>
                    </b-row>
                  </b-container>
                </b-card>
              </b-container>
            </b-col>
          </b-row>

          <!-- Files -->
          <b-row>
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <b-row
                    align-v="center"
                    align-h="between"
                    no-gutters
                    class="section-header-row"
                  >
                    <b-col cols="auto">
                      <h3 class="font-weight-bold">Files:</h3>
                    </b-col>
                  </b-row>

                  <!-- No separate Archived tab here (unlike the owner's
                       ViewTradie): this file list is already scoped to the
                       current shared project by URL, so an archived
                       (company_approval = not_applicable) row here belongs to
                       this project and is shown inline in the single table
                       below, badged as "Archived". -->

                  <!-- Mobile: stacked file cards (hidden on md+) -->
                  <div class="d-md-none mobile-file-list mt-2">
                    <div
                      v-for="item in displayedFiles"
                      :key="item.id"
                      class="mobile-file-item"
                      :class="mobileFileAttentionClass(item)"
                      @click="viewFile(item)"
                    >
                      <div class="d-flex align-items-start">
                        <div class="flex-grow-1" style="min-width: 0">
                          <div class="mobile-file__name">
                            {{ item.name || '-' }}
                          </div>
                          <div class="mobile-file__meta">
                            <span>
                              <span class="mobile-file__label">Issued</span>
                              {{ item.date_issued || '-' }}
                            </span>
                            <span>
                              <span class="mobile-file__label">Expiry</span>
                              {{ item.date_expiry || '-' }}
                            </span>
                            <span>
                              <span class="mobile-file__label">Visibility</span>
                              {{ formatFileVisibilityDisplay(item) }}
                            </span>
                            <span class="mobile-file__approval">
                              <span class="mobile-file__label">Approval</span>
                              <company-approval-badge
                                :status="item && item.company_approval"
                                :reason="
                                  (item && item.company_approval_reason) || ''
                                "
                                :set-date="
                                  (item && item.company_approval_at) || ''
                                "
                                :can-edit="false"
                              />
                            </span>
                          </div>
                        </div>
                        <div
                          class="mobile-file__status"
                          v-b-tooltip.hover="getStatusCellTooltip(item)"
                        >
                          <b-badge
                            v-if="
                              getTradieProfileFileAttentionLevel(item) ===
                              'expired'
                            "
                            pill
                            variant="danger"
                            class="tradie-file-status-badge"
                            >Expired</b-badge
                          >
                          <b-badge
                            v-else-if="
                              getTradieProfileFileAttentionLevel(item) ===
                              'expiring_soon'
                            "
                            pill
                            variant="warning"
                            class="tradie-file-status-badge tradie-file-status-badge--soon"
                            >Expiring soon</b-badge
                          >
                          <span v-else class="mobile-file__status-text">{{
                            formatTradieFilesStatusDisplay(item && item.status)
                          }}</span>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="!displayedFiles || !displayedFiles.length"
                      class="text-center py-4 text-muted"
                    >
                      There are no files to show.
                    </div>
                  </div>

                  <!-- Desktop: full table (hidden below md) -->
                  <div class="d-none d-md-block">
                    <b-table
                      id="shared-tradie-files"
                      class="mt-2"
                      responsive
                      hover
                      show-empty
                      empty-text="There are no files to show."
                      :fields="tradieFilesTableFields"
                      :items="displayedFiles"
                      :tbody-tr-class="profileFileAttentionRowClass"
                      @row-clicked="viewFile"
                    >
                      <template #cell(status)="{ item }">
                        <span
                          class="tradie-file-status-cell"
                          v-b-tooltip.hover="getStatusCellTooltip(item)"
                        >
                          <b-badge
                            v-if="
                              getTradieProfileFileAttentionLevel(item) ===
                              'expired'
                            "
                            pill
                            variant="danger"
                            class="tradie-file-status-badge"
                            >Expired</b-badge
                          >
                          <b-badge
                            v-else-if="
                              getTradieProfileFileAttentionLevel(item) ===
                              'expiring_soon'
                            "
                            pill
                            variant="warning"
                            class="tradie-file-status-badge tradie-file-status-badge--soon"
                            >Expiring soon</b-badge
                          >
                          <template v-else>
                            {{
                              formatTradieFilesStatusDisplay(
                                item && item.status,
                              )
                            }}
                          </template>
                        </span>
                      </template>

                      <template #cell(company_approval)="{ item }">
                        <company-approval-badge
                          :status="item && item.company_approval"
                          :reason="(item && item.company_approval_reason) || ''"
                          :set-date="(item && item.company_approval_at) || ''"
                          :can-edit="false"
                        />
                      </template>
                    </b-table>
                  </div>
                </b-container>
              </b-card>
            </b-container>
          </b-row>

          <!-- "Required Files (by Trades)" and "Requested Files" are
               intentionally omitted from the shared-project view — a sharee
               only sees the tradie's profile, trades and uploaded files. -->
        </b-container>

        <!-- View File Modal (read-only) -->
        <ViewFileModal
          v-if="isViewingFile && viewFileId != null"
          :showModal="isViewingFile"
          :fileId="viewFileId"
          :tradieId="tradieId"
          :shareId="shareId"
          :readOnly="true"
          @close="onViewFileClose"
        />
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ViewFileModal from '@/components/modals/ViewFileModal.vue'
import CompanyApprovalBadge from '@/components/badges/CompanyApprovalBadge.vue'
import { formatFileVisibilityDisplay } from '@/utils/fileLabels'
import {
  formatTradieFileStatusColumnDisplay,
  formatTradieFilesStatusDisplay,
  getTradieProfileFileAttentionLevel,
  isTradieFileMarkedNa,
  formatStatusSetDate,
} from '@/utils/fileAttention'
import { formatPhoneWithDialCode } from '@/utils/phonePolicy'
import { parseServerDate } from '@/utils/dateHelper'

export default {
  name: 'SharedViewTradie',

  components: {
    ViewFileModal,
    CompanyApprovalBadge,
  },

  props: {
    shareId: {
      type: [String, Number],
      default: null,
    },
    tradieId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      share: null,
      isLoading: false,
      loadError: '',

      isViewingFile: false,
      viewFileId: null,
    }
  },

  computed: {
    // Set while the tradie is inside their account-deletion notice period.
    accountDeletionDate() {
      const at = this.tradie && this.tradie.deletion_scheduled_at
      const d = at ? parseServerDate(at) : null
      return d
        ? d.toLocaleDateString('en-AU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : ''
    },
    ...mapGetters('project', ['tradie', 'files']),

    ...mapGetters('trade', ['trades']),

    /**
     * Files shown to the shared-in company. This list comes from an endpoint
     * that is already scoped to the current shared project by URL
     * (projects/{projectId}/tradie/{tradieId}), so every row here belongs to
     * (or has its company_approval decided for) this project — including
     * archived (company_approval = not_applicable) ones, which are shown.
     * This is not a global archived-files view; a file archived only on a
     * different project never reaches this list in the first place. The
     * legacy is_na flag is a separate, unrelated concept (the file's own
     * Tradie Pass verification status) and stays excluded.
     */
    displayedFiles() {
      return (this.files || []).filter((f) => f && !f.is_na)
    },

    /** Uploaded files on the tradie's profile — includes visibility + company approval. */
    tradieFilesTableFields() {
      return [
        { key: 'name', label: 'File Name', class: 'w-25', sortable: true },
        { key: 'date_issued', label: 'Issued Date', sortable: true },
        { key: 'date_expiry', label: 'Expiry Date', sortable: true },
        {
          key: 'visibility',
          label: 'Visibility',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          formatter: (_value, _key, item) => formatFileVisibilityDisplay(item),
        },
        { key: 'status', label: 'Tradie Pass Verification', sortable: true },
        {
          key: 'company_approval',
          label: 'Company Approval',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'company-approval-cell',
        },
      ]
    },

    breadcrumbs() {
      return [
        { text: 'Projects', to: '/projects' },
        {
          text: 'Shared Project',
          to: {
            name: 'projects.shared-view-page',
            params: { shareId: String(this.shareId) },
          },
        },
        { text: this.tradie ? this.tradie.full_name || '-----' : '' },
      ]
    },

    tradeNames() {
      // Gate on trade_ids — empty means no trades assigned within this project,
      // mirroring ViewTradie.vue's empty state.
      if (!this.tradie) return []
      const ids = this.tradie.trade_ids || []
      if (!ids.length) return []

      // Prefer mapping via the company's trades list (works for the project owner).
      if (this.trades && this.trades.length) {
        const matched = this.trades
          .filter((x) =>
            ids.includes(
              `${x.company_id}-${x.company_id != null ? x.trade_id : x.id}`,
            ),
          )
          .map((x) => x.name)
        if (matched.length) return matched
      }

      // Fallback: sharees don't have the sharer's company trades, but the API
      // returns tradie.trade_names parallel to trade_ids (same index = same trade).
      const apiNames = this.tradie.trade_names
      if (Array.isArray(apiNames) && apiNames.length === ids.length) {
        return apiNames
      }
      if (typeof apiNames === 'string') {
        const parts = apiNames
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        if (parts.length === ids.length) return parts
      }
      return []
    },
  },

  methods: {
    ...mapActions('project', ['getProjectShare', 'getTradie']),

    formatTradieFileStatusColumnDisplay,
    formatTradieFilesStatusDisplay,
    getTradieProfileFileAttentionLevel,
    isTradieFileMarkedNa,
    formatFileVisibilityDisplay,
    formatPhoneWithDialCode,

    /** Row tint class for the mobile file cards (mirrors ViewTradie.vue). */
    mobileFileAttentionClass(item) {
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired') return 'mobile-file-item--expired'
      if (level === 'expiring_soon') return 'mobile-file-item--soon'
      return ''
    },

    getStatusCellTooltip(item) {
      if (!item) return { title: '', disabled: true }
      const statusLabel = this.tradieFileStatusLabel(item)
      if (!statusLabel) return { title: '', disabled: true }
      let reason = ''
      const raw = item.status_reason
      if (raw && raw !== '-') {
        const trimmed = String(raw).trim()
        if (trimmed) reason = trimmed
      }
      const setDate = formatStatusSetDate(item.status_updated_at)
      let title = reason ? `${statusLabel}: ${reason}` : statusLabel
      if (setDate) title += ` - ${setDate}`
      return { title, disabled: false }
    },
    tradieFileStatusLabel(item) {
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired') return 'Expired'
      if (level === 'expiring_soon') return 'Expiring soon'
      const raw = formatTradieFilesStatusDisplay(item && item.status)
      if (raw == null || raw === '') return ''
      return String(raw).replace(/\b\w/g, (c) => c.toUpperCase())
    },

    profileFileAttentionRowClass(item, type) {
      if (!item || type !== 'row') return 'cursor-pointer'
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired') {
        return 'cursor-pointer bg-alert tradie-file-row-attention'
      }
      if (level === 'expiring_soon') {
        return 'cursor-pointer bg-highlight tradie-file-row-attention'
      }
      return 'cursor-pointer'
    },

    async fetchAll() {
      this.isLoading = true
      this.loadError = ''
      try {
        const share = await this.getProjectShare(this.shareId)
        this.share = share || null
        const projectId =
          share && share.project && share.project.id ? share.project.id : null
        if (!projectId) {
          this.loadError = 'This shared project is no longer available.'
          return
        }
        await this.getTradie({ id: projectId, tradieId: this.tradieId })
      } catch (err) {
        const status = err && err.status
        if (status === 403) {
          this.loadError =
            "You don't have access to this tradie. The share may have been removed or expired."
        } else if (status === 404) {
          this.loadError = 'This tradie could not be found.'
        } else {
          this.loadError = 'Could not load this tradie.'
        }
      } finally {
        this.isLoading = false
      }
    },

    viewFile(item) {
      if (!item || item.id == null) return
      this.viewFileId = item.id
      this.isViewingFile = true
    },

    onViewFileClose() {
      this.isViewingFile = false
      this.viewFileId = null
    },

    gotoProject() {
      if (!this.$router) return
      this.$router.push({
        name: 'projects.shared-view-page',
        params: { shareId: String(this.shareId) },
      })
    },

    dateFormat(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
  },

  mounted() {
    this.fetchAll()
  },
}
</script>

<style lang="scss" scoped>
/deep/ .cursor-pointer {
  cursor: pointer;
}

.tradie-file-status-cell {
  display: inline-block;
}

.tradie-file-status-badge {
  font-weight: 600;
}

/* Expiring-soon badge: yellow outline (same as project view), filled to
   match the row tint so the badge blends with the highlighted row. */
.tradie-file-status-badge--soon {
  background-color: #fff8e1 !important;
  color: #856404 !important;
  border: 1px solid #ffc107;
}

/deep/ tr.tradie-file-row-attention.bg-alert,
/deep/ tr.tradie-file-row-attention.bg-alert:hover {
  background-color: #ffeaea !important;
}

/deep/ tr.tradie-file-row-attention.bg-highlight,
/deep/ tr.tradie-file-row-attention.bg-highlight:hover {
  background-color: #fff8e1 !important;
}

.consent-hidden-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background-color: #fff8ec;
  border: 1px solid #f5dfae;
  border-left: 4px solid #e2a53a;
  border-radius: 6px;
  padding: 0.85rem 1rem;
  color: #7a5a1e;
  font-size: 0.875rem;

  &__icon {
    flex-shrink: 0;
    margin-top: 0.15rem;
    font-size: 1.1rem;
    color: #e2a53a;
  }

  p {
    color: inherit;
  }
}

/* ---------------------------------------------------------------------------
 * Mobile file cards — below md the Files / Required Files / Requested Files
 * tables are replaced by compact stacked cards (one file per card) so nothing
 * scrolls sideways. Mirrors ViewTradie.vue's owner-company treatment.
 * ------------------------------------------------------------------------- */
.mobile-file-list {
  border-top: 1px solid #e1e6ea;
}

.mobile-file-item {
  padding: 0.65rem 0.25rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
  cursor: pointer;
}

.mobile-file-item--expired {
  background-color: #ffeaea;
}

.mobile-file-item--soon {
  background-color: #fff8e1;
}

.mobile-file__name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #364052;
  line-height: 1.3;
  word-break: break-word;
}

.mobile-file__meta {
  font-size: 0.75rem;
  color: #8b95a3;
  margin-top: 0.2rem;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
}

.mobile-file__label {
  font-weight: 600;
  color: #6c7581;
  margin-right: 0.15rem;
}

.mobile-file__approval {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.1rem;
}

.mobile-file__status {
  flex-shrink: 0;
  margin-left: 0.5rem;
  text-align: right;
}

.mobile-file__status-text {
  font-size: 0.72rem;
  color: #6c7581;
  text-transform: capitalize;
}

/* ---------------------------------------------------------------------------
 * Small-screen responsiveness (down to 320px). All rules live inside
 * max-width media queries so the desktop layout is untouched. Mirrors
 * ViewTradie.vue so the shared-project route matches the owner route.
 * ------------------------------------------------------------------------- */

/* Tablet / large phone: trim oversized vertical spacing. */
@media (max-width: 767.98px) {
  .tradie-profile-view {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
  }

  /deep/ .breadcrumb {
    padding-top: 2rem !important;
    margin-top: 2rem !important;
  }

  .profile-detail-flex {
    gap: 1.5rem !important;
  }
}

/* Phones: stack sections and reclaim padding. */
@media (max-width: 575.98px) {
  .tradie-profile-view {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }

  /deep/ .breadcrumb {
    padding-top: 1rem !important;
    margin-top: 1rem !important;
    font-size: 0.85rem;
  }

  /* Title + "Back to Project" stack vertically. */
  .profile-header-row {
    flex-direction: column;

    > .col,
    > [class*='col-'] {
      flex: 0 0 100%;
      max-width: 100%;
      text-align: left !important;
    }

    h2 {
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }

    /* Full-width so it lines up flush with the cards below it, rather than
       sizing to its label and sitting narrow/left. */
    .btn {
      display: block;
      width: 100%;
      max-width: none !important;
      margin-top: 0.25rem;
    }
  }

  /* Avatar sits above the profile detail grid. */
  .profile-detail-flex {
    flex-direction: column;
    gap: 1rem !important;
    margin-top: 1rem !important;
  }

  /* Reclaim horizontal space from card padding. */
  /deep/ .file-details > .card-body {
    padding: 1rem;
  }

  /* Profile details: 2-column grid. Long values span full width; short
     values (dates, toggles, etc.) pair up side-by-side. */
  .profile-details-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1rem;
    row-gap: 0.65rem;

    > * {
      grid-column: 1 / -1;
      margin-bottom: 0 !important;
      font-size: 0.85rem;
      line-height: 1.35;
    }

    > .profile-col-half {
      grid-column: span 1;
    }

    .font-weight-bold {
      display: block;
      font-size: 0.66rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #8b95a3;
      margin-bottom: 1px;
    }

    br {
      display: none;
    }

    h4 {
      font-size: 0.95rem;
      margin-top: 0.35rem;
      margin-bottom: 0.15rem;
    }
  }

  /* Section title rows: tighten wrapping gap. */
  .section-header-row {
    row-gap: 0.75rem;
  }

  /* Trades section: the badges column drops to its own full-width row below
     the "Trades:" heading instead of being squeezed beside it. */
  .trades-section-row {
    flex-wrap: wrap;

    > .col {
      order: 3;
      flex: 0 0 100%;
      max-width: 100%;
      padding-left: 0 !important;
      padding-right: 0 !important;
      margin-top: 0.5rem;
    }

    > .col-auto:first-child {
      order: 1;
      flex: 1 1 auto;
    }
  }

  h3 {
    font-size: 1.25rem;
  }
  h4 {
    font-size: 1.1rem;
  }
}

/* Smallest phones (<=~360px): shrink the avatar so it never crowds the edges. */
@media (max-width: 359.98px) {
  .profile-detail-flex /deep/ .b-avatar {
    width: 140px !important;
    height: 187px !important;
  }
}
</style>
